import {
  Activity,
  BookUser,
  Cable,
  Compass,
  Feather,
  Microscope,
  Send,
  ShieldCheck
} from "lucide-react";
import type { Agent } from "../types/platform";

// Array order sets ring position; `order` is the loop sequence and is
// independent of it.
export const agents: readonly Agent[] = [
  {
    name: "Plan",
    order: 3,
    icon: Compass,
    subtitle: "Plans each worker's training.",
    why: "Most companies can't say what training a given worker needs right now. Assignments follow job titles and calendars, not risk or readiness. Someone has to make that call for every worker, every day, and no L&D team has the hours.",
    description:
      "Decides what training each worker needs and when. Matches workforce needs to the right training at business speed, weighing role, risk, readiness, and evidence.",
    how: "Sources off-the-shelf training through Marketplace and Atlas, and custom content from Simon. Signal fusion across learner profile, content graph, and business systems."
  },
  {
    name: "Build",
    order: 4,
    icon: Feather,
    subtitle: "Builds what's missing.",
    why: "When the right course doesn't exist, custom content takes months and an outside vendor. By then the need has moved. Companies need training built on their own procedures at the speed the business changes.",
    description:
      "Authors and produces training content using instructional design grounded in learning science.",
    how: "Hybrid Lookbook pipeline with eight specialized agents. Restate for durability, Langfuse for observability, human approval gates."
  },
  {
    name: "Deliver",
    order: 5,
    icon: Send,
    subtitle: "Delivers it anywhere.",
    why: "Training that won't launch doesn't count. Customers run many different LMSs and devices, and every failed launch is a support ticket and a missing record. Delivery is also where the evidence trail starts.",
    description:
      "Delivers training to any device or LMS through standards-compliant, sub-100ms playback.",
    how: "Rust/Axum delivery engine, lightweight TypeScript client, SCORM 1.2 / 2004 and LTI 1.3 support, full event telemetry."
  },
  {
    name: "Decode",
    order: 2,
    icon: Microscope,
    subtitle: "Decodes every course.",
    why: "A course package is a black box. A title and a description can't tell you what it teaches or tests. You can't match training to a skill gap until you know what is inside the course.",
    description:
      "Takes compiled course packages apart and turns them into structured, searchable knowledge: objectives, topics, assessments, skills.",
    how: "Two-pass pipeline: deterministic extraction, then AI enrichment. Parser registry for Storyline, Rise, Captivate, plus generic HTML fallback."
  },
  {
    name: "Connect",
    order: 1,
    icon: Cable,
    subtitle: "Connects to your HRIS.",
    why: "Every decision starts with who the workers are, what roles they hold, and who they report to. That lives in the HRIS. Without a live connection, setup is manual work and the data is stale the day it loads. This is what lets OpenSesame arrive finished.",
    description:
      "Syncs learners, roles, and organizational context from HRIS and business systems into OpenSesame.",
    how: "Multi-tenant broker with field-level AES-256-GCM encryption, HMAC-SHA256 blind indexes, and tenant-isolated keys."
  },
  {
    name: "Watch",
    order: 8,
    icon: Activity,
    subtitle: "Watches itself.",
    why: "A system that runs itself has to watch itself. Agents cost money on every call and can fail quietly. Staff need to see health and cost before a customer feels it.",
    description:
      "Tracks service health, cost, and performance across every application.",
    how: "Langfuse for AI observability, per-workflow cost accounting, incident response tooling across all cells."
  },
  {
    name: "Verify",
    order: 6,
    icon: ShieldCheck,
    subtitle: "Verifies the skill.",
    why: "Completion is not capability. A finished course shows someone sat through it. In work where people can get hurt, the company needs to know the person can do the job. This is the Earned part of proof.",
    description:
      "Verifies competency through voice-based assessment. Confirms what a learner can actually do, not just what they completed.",
    how: "Powers Coach. Conversational evaluation against role-and-skill rubrics, with signals back into the learner profile for continuous re-verification."
  },
  {
    name: "Prove",
    order: 7,
    icon: BookUser,
    subtitle: "Proves it wherever the worker goes.",
    why: "A worker's proven skills are scattered across systems and lost at every job change. The record should belong to both the worker and the company, and the worker should carry it forward. This is the Yours part of proof.",
    description:
      "Keeps the verified record of a worker's skills, credentials, and competencies. The record is shared by the worker and the company, and it is portable across employers and systems.",
    how: "Powers Proof. Cryptographically verifiable credential wallet, tied to HRIS through Conduit, surfaced to workers in the B2proC layer."
  }
] as const;
